import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useRegisterSharedMenuFilters } from './FilterBridgeContext';
import {
  lastDaysEndingYesterday,
  periodToIsoRange,
  todayIso,
  yesterdayRange,
} from '../utils/period';

const DateFilterContext = createContext(null);

export function DateFilterProvider({ children }) {
  const initHist = yesterdayRange();
  const [viewMode, setViewMode] = useState('live');
  const [period, setPeriod] = useState('today');
  const [activeDatePreset, setActiveDatePreset] = useState('today');
  const [draftFrom, setDraftFrom] = useState(initHist.from);
  const [draftTo, setDraftTo] = useState(initHist.to);
  const [appliedFrom, setAppliedFrom] = useState(initHist.from);
  const [appliedTo, setAppliedTo] = useState(initHist.to);

  const historicalMode = viewMode === 'historical';
  const liveRange = periodToIsoRange(period);
  const queryFrom = historicalMode ? appliedFrom : liveRange.from;
  const queryTo = historicalMode ? appliedTo : liveRange.to;

  const applyHistorical = useCallback((from, to) => {
    if (from) setAppliedFrom(from);
    if (to) setAppliedTo(to);
    setViewMode('historical');
  }, []);

  const onDatePreset = useCallback((preset) => {
    if (preset === 'today') {
      setViewMode('live');
      setPeriod('today');
      setActiveDatePreset('today');
      return;
    }
    if (preset === 'yesterday') {
      const range = yesterdayRange();
      setDraftFrom(range.from);
      setDraftTo(range.to);
      setAppliedFrom(range.from);
      setAppliedTo(range.to);
      setViewMode('historical');
      setActiveDatePreset('yesterday');
      return;
    }
    if (preset === 'last7') {
      const range = lastDaysEndingYesterday(7);
      setDraftFrom(range.from);
      setDraftTo(range.to);
      setAppliedFrom(range.from);
      setAppliedTo(range.to);
      setViewMode('historical');
      setActiveDatePreset('last7');
      return;
    }
    if (preset === 'last30') {
      const range = lastDaysEndingYesterday(30);
      setDraftFrom(range.from);
      setDraftTo(range.to);
      setAppliedFrom(range.from);
      setAppliedTo(range.to);
      setViewMode('historical');
      setActiveDatePreset('last30');
      return;
    }
    if (preset === 'custom') {
      setViewMode('historical');
      setActiveDatePreset('custom');
    }
  }, []);

  const applyCustomRange = useCallback(() => {
    setAppliedFrom(draftFrom);
    setAppliedTo(draftTo);
    setViewMode('historical');
    setActiveDatePreset('custom');
  }, [draftFrom, draftTo]);

  const value = useMemo(
    () => ({
      viewMode,
      setViewMode,
      period,
      setPeriod,
      historicalMode,
      liveRange,
      queryFrom,
      queryTo,
      draftFrom,
      setDraftFrom,
      draftTo,
      setDraftTo,
      appliedFrom,
      appliedTo,
      activeDatePreset,
      onDatePreset,
      applyHistorical,
      applyCustomRange,
      maxPastDate: todayIso(),
    }),
    [
      viewMode,
      period,
      historicalMode,
      liveRange,
      queryFrom,
      queryTo,
      draftFrom,
      draftTo,
      appliedFrom,
      appliedTo,
      activeDatePreset,
      onDatePreset,
      applyHistorical,
      applyCustomRange,
    ]
  );

  const menuDateConfig = useMemo(
    () => ({
      viewMode,
      setViewMode,
      historicalMode,
      activeDatePreset,
      onDatePreset,
      draftFrom,
      draftTo,
      setDraftFrom,
      setDraftTo,
      onApplyHistorical: applyCustomRange,
      maxPastDate: todayIso(),
    }),
    [
      viewMode,
      setViewMode,
      historicalMode,
      activeDatePreset,
      onDatePreset,
      draftFrom,
      draftTo,
      setDraftFrom,
      setDraftTo,
      applyCustomRange,
    ]
  );

  useRegisterSharedMenuFilters(menuDateConfig);

  return <DateFilterContext.Provider value={value}>{children}</DateFilterContext.Provider>;
}

export function useDateFilter() {
  const ctx = useContext(DateFilterContext);
  if (!ctx) throw new Error('useDateFilter must be used within DateFilterProvider');
  return ctx;
}
